import { ACTION_PREFIX, EVENT_PREFIX } from '@unifyapps/carbon/no-code/const';
import { InterfaceModes } from '@unifyapps/carbon/no-code/stores/InterfaceStore';
import { getIdWithPrefix } from '@unifyapps/carbon/utils/id';
import type { ImageContent } from '@unifyapps/defs/blocks/Image/types';
import { ImageSrcEnum } from '@unifyapps/defs/blocks/Image/types';
import { ActionType } from '@unifyapps/defs/types/action';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { extractExtensionFromPath } from '@unifyapps/network/asset-cache/utils';
import isUndefined from 'lodash/isUndefined';
import { resolveFileTypeFromUploadDetails } from '../utils/resolveFileTypeFromUploadDetails';

export function getContentFit(objectFit: string | undefined) {
  switch (objectFit) {
    case 'object-contain':
      return 'contain';
    case 'object-cover':
      return 'cover';
    case 'object-fill':
      return 'fill';
    case 'object-scale-down':
      return 'scale-down';
    case 'object-none':
      return 'none';
    default:
      return 'contain';
  }
}

export function getOpenMediaViewerEvent(blockId: string) {
  return {
    id: getIdWithPrefix(EVENT_PREFIX),
    eventType: EventTypeEnum.OnClick,
    action: {
      id: getIdWithPrefix(ACTION_PREFIX),
      actionType: ActionType.ControlBlockMethod,
      payload: { blockId, methodName: 'openInMediaViewer' },
    },
  };
}

export const getAdaptedSrcAndAlt = (content: ImageContent) => {
  const { data, alt, src } = content;

  // For backward compatibility. Use data properties if they exist in content.
  // Otherwise, fall back to the top-level src and alt values. Going forward,
  // image src and alt will be stored in the data object.
  if (data) {
    if (data.type === ImageSrcEnum.URL) {
      return {
        src: data.src,
        alt: data.alt,
        fileType: extractExtensionFromPath(data.src),
      };
    }
    return {
      src: data.uploadDetails?.url,
      alt: data.alt,
      fileType: resolveFileTypeFromUploadDetails(data.uploadDetails),
    };
  }

  return { src, alt, fileType: extractExtensionFromPath(src) };
};

export const getRenderEmptyState = (content: ImageContent, mode: InterfaceModes) => {
  const { data } = content;
  const { type, uploadDetails, src } = data ?? {};
  const isBuilder = mode === InterfaceModes.BUILDER;

  if (!isBuilder) {
    return false;
  }

  if (
    type === ImageSrcEnum.UPLOAD &&
    (isUndefined(uploadDetails) || uploadDetails?.isUploaded === false)
  ) {
    return true;
  }

  if (type === ImageSrcEnum.URL && isUndefined(src)) {
    return true;
  }

  return false;
};
